import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  Hourglass,
  FolderOpen,
  RefreshCw,
  MessageSquarePlus,
  PauseCircle,
  ShieldAlert,
  CheckCircle2,
  Ban,
  Tag,
  Eye,
  EyeOff,
  AlertTriangle,
  X,
  AlertCircle,
  FileText,
  Image as ImageIcon,
  Download,
  Trash2,
} from "lucide-react";
import { useTheme } from "../../context/themeContext";
import serverUrl from "../../utils/utilsser";

// ---------------------------------------------------------------------------
// Brand tokens — single source of truth. Both GrievancesDashboard.jsx and
// GrievanceCaseDetail.jsx import everything from this file.
// ---------------------------------------------------------------------------

export const BRAND = {
  gradient: "linear-gradient(90deg, #ef5da8 0%, #8b7cf6 100%)",
  navy: "#3d3a6b",
  teal: "#34d9b8",
  pink: "#ef5da8",
  purple: "#8b7cf6",
  gold: "#f6ad3c",
  silver: "#9aa3b5",
  bronze: "#c98a4b",
  blue: "#5b8def",
  red: "#ef4444",
};

export const TOKENS = {
  dark: {
    isDark: true,
    pageWash:
      "bg-[radial-gradient(circle_at_15%_0%,rgba(139,124,246,0.10),transparent_45%),radial-gradient(circle_at_85%_10%,rgba(239,93,168,0.08),transparent_40%)]",
    card: "bg-[#13152c]/90 border-white/[0.06]",
    cardShadow:
      "shadow-[0_1px_0_rgba(255,255,255,0.04)_inset,0_20px_50px_-20px_rgba(0,0,0,0.65)]",
    textPrimary: "text-white",
    textMuted: "text-slate-400",
    textFaint: "text-slate-500",
    surface: "bg-white/[0.04]",
    surfaceSoft: "bg-white/[0.03]",
    surfaceHover: "hover:bg-white/[0.06]",
    pillBorder: "border-white/10",
    gridStroke: "rgba(255,255,255,0.07)",
    overlay: "bg-black/60",
    rowHover: "hover:bg-white/[0.035]",
    rowBorder: "border-white/[0.06]",
    inputBg: "bg-white/[0.06]",
  },
  light: {
    isDark: false,
    pageWash:
      "bg-[radial-gradient(circle_at_10%_-5%,rgba(239,93,168,0.08),transparent_42%),radial-gradient(circle_at_90%_0%,rgba(139,124,246,0.10),transparent_42%)]",
    card: "bg-white/95 border-black/[0.06]",
    cardShadow:
      "shadow-[0_1px_0_rgba(255,255,255,0.6)_inset,0_20px_44px_-24px_rgba(31,41,74,0.22)]",
    textPrimary: "text-slate-900",
    textMuted: "text-slate-500",
    textFaint: "text-slate-400",
    surface: "bg-black/[0.035]",
    surfaceSoft: "bg-black/[0.03]",
    surfaceHover: "hover:bg-black/[0.045]",
    pillBorder: "border-black/10",
    gridStroke: "rgba(15,23,42,0.08)",
    overlay: "bg-slate-900/50",
    rowHover: "hover:bg-black/[0.02]",
    rowBorder: "border-black/[0.05]",
    inputBg: "bg-black/[0.04]",
  },
};

export const ACCENT_GLOW = {
  purple: {
    dark: "hover:shadow-[0_24px_60px_-18px_rgba(139,124,246,0.45)] hover:border-[#8b7cf6]/30",
    light: "hover:shadow-[0_24px_50px_-20px_rgba(139,124,246,0.35)] hover:border-[#8b7cf6]/30",
  },
  teal: {
    dark: "hover:shadow-[0_24px_60px_-18px_rgba(52,217,184,0.4)] hover:border-[#34d9b8]/30",
    light: "hover:shadow-[0_24px_50px_-20px_rgba(52,217,184,0.3)] hover:border-[#34d9b8]/30",
  },
  pink: {
    dark: "hover:shadow-[0_24px_60px_-18px_rgba(239,93,168,0.4)] hover:border-[#ef5da8]/30",
    light: "hover:shadow-[0_24px_50px_-20px_rgba(239,93,168,0.3)] hover:border-[#ef5da8]/30",
  },
  gold: {
    dark: "hover:shadow-[0_24px_60px_-18px_rgba(246,173,60,0.4)] hover:border-[#f6ad3c]/30",
    light: "hover:shadow-[0_24px_50px_-20px_rgba(246,173,60,0.3)] hover:border-[#f6ad3c]/30",
  },
  red: {
    dark: "hover:shadow-[0_24px_60px_-18px_rgba(239,68,68,0.4)] hover:border-[#ef4444]/30",
    light: "hover:shadow-[0_24px_50px_-20px_rgba(239,68,68,0.3)] hover:border-[#ef4444]/30",
  },
};

export function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false,
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

export function CardShell({ children, className = "", accent = "purple", padded = true }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const glow = ACCENT_GLOW[accent][isDark ? "dark" : "light"];
  return (
    <div
      className={
        "rounded-2xl sm:rounded-3xl border backdrop-blur-sm transition-all duration-300 " +
        (padded ? "p-4 sm:p-5 md:p-6 " : "") +
        t.card +
        " " +
        t.cardShadow +
        " " +
        glow +
        " " +
        className
      }
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Domain constants.
// ---------------------------------------------------------------------------

export const STATUS_META = {
  pending: { label: "Pending", tint: BRAND.gold, icon: Hourglass },
  open: { label: "Open", tint: BRAND.blue, icon: FolderOpen },
  in_progress: { label: "Under Investigation", tint: BRAND.purple, icon: RefreshCw },
  awaiting_response: { label: "Awaiting Employee Response", tint: BRAND.pink, icon: MessageSquarePlus },
  on_hold: { label: "On Hold", tint: BRAND.silver, icon: PauseCircle },
  escalated: { label: "Escalated", tint: BRAND.red, icon: ShieldAlert },
  resolved: { label: "Resolved", tint: BRAND.teal, icon: CheckCircle2 },
  closed: { label: "Closed", tint: BRAND.bronze, icon: Ban },
};

export const PRIORITY_META = {
  high: { label: "High", tint: BRAND.red },
  medium: { label: "Medium", tint: BRAND.gold },
  low: { label: "Low", tint: BRAND.teal },
  none: { label: "None", tint: BRAND.silver },
};

export const TRACK_STEPS = [
  { key: "pending", label: "Filed", icon: Hourglass },
  { key: "open", label: "Under Review", icon: FolderOpen },
  { key: "in_progress", label: "Investigating", icon: RefreshCw },
  { key: "resolved", label: "Resolved", icon: CheckCircle2 },
];

export const PAGE_SIZE = 10;

const CATEGORY_COLORS = [BRAND.purple, BRAND.pink, BRAND.blue, BRAND.gold, BRAND.bronze, BRAND.teal, BRAND.silver];

function hashCode(str = "") {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h << 5) - h + str.charCodeAt(i);
  return h;
}

export function categoryTint(category) {
  const idx = Math.abs(hashCode(category)) % CATEGORY_COLORS.length;
  return CATEGORY_COLORS[idx];
}

export function fmtDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function fmtDateTime(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return (
    d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) +
    " · " +
    d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
  );
}

export function fmtRelative(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  const diffMs = Date.now() - d.getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return fmtDate(dateStr);
}

export function dayKey(dateStr) {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  return d.toDateString();
}

export function fmtDayLabel(dateStr) {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

export function fmtBytes(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function isImageMime(mimetype = "") {
  return mimetype.startsWith("image/");
}

// ---------------------------------------------------------------------------
// API helper — every call goes through here so auth + error handling stay
// consistent. Accepts a plain object body (sent as JSON) or a FormData body
// (sent as multipart, e.g. for file uploads).
// ---------------------------------------------------------------------------

export async function apiRequest(path, { method = "GET", body, token } = {}) {
  const isFormData = typeof FormData !== "undefined" && body instanceof FormData;
  const res = await fetch(`${serverUrl}${path}`, {
    method,
    headers: {
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body === undefined ? undefined : isFormData ? body : JSON.stringify(body),
  });
  let json = {};
  try {
    json = await res.json();
  } catch {
    // no-op — some error responses may not return JSON
  }
  if (!res.ok || json.success === false) {
    throw new Error(json.message || "Something went wrong. Please try again.");
  }
  return json.data;
}

// ---------------------------------------------------------------------------
// Small shared UI atoms
// ---------------------------------------------------------------------------

export function StatusBadge({ status, size = "md" }) {
  const meta = STATUS_META[status] ?? STATUS_META.pending;
  const Icon = meta.icon;
  const small = size === "sm";
  return (
    <span
      className={
        "inline-flex items-center gap-1.5 rounded-full font-semibold " +
        (small ? "px-2.5 py-1 text-[11px]" : "px-3 py-1.5 text-xs")
      }
      style={{ backgroundColor: `${meta.tint}1f`, color: meta.tint }}
    >
      <Icon size={small ? 11 : 13} strokeWidth={2.5} />
      {meta.label}
    </span>
  );
}

export function PriorityBadge({ priority }) {
  const meta = PRIORITY_META[priority] ?? PRIORITY_META.none;
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold"
      style={{ backgroundColor: `${meta.tint}1f`, color: meta.tint }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: meta.tint }} />
      {meta.label}
    </span>
  );
}

export function CategoryTag({ category }) {
  const tint = categoryTint(category);
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium"
      style={{ backgroundColor: `${tint}14`, borderColor: `${tint}33`, color: tint }}
    >
      <Tag size={10} strokeWidth={2.5} />
      {category}
    </span>
  );
}

export function ShareBadge({ shared }) {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold"
      style={{
        backgroundColor: shared ? `${BRAND.teal}22` : `${BRAND.silver}22`,
        color: shared ? BRAND.teal : BRAND.silver,
      }}
    >
      {shared ? <Eye size={10} strokeWidth={2.5} /> : <EyeOff size={10} strokeWidth={2.5} />}
      {shared ? "Shared with Employee" : "Private / Not Shared"}
    </span>
  );
}

export function Toast({ toast, onDismiss }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  if (!toast) return null;
  const isError = toast.type === "error";
  const tint = isError ? BRAND.red : BRAND.teal;
  const Icon = isError ? AlertTriangle : CheckCircle2;
  return (
    <div className="fixed inset-x-0 bottom-5 z-[70] flex justify-center px-4">
      <div
        className={
          "flex items-center gap-2.5 rounded-full border px-4 py-2.5 text-sm font-medium shadow-lg backdrop-blur-sm transition-all " +
          t.card +
          " " +
          t.cardShadow
        }
      >
        <span style={{ color: tint }}>
          <Icon size={16} strokeWidth={2.5} />
        </span>
        <span className={t.textPrimary}>{toast.message}</span>
        <button onClick={onDismiss} className={t.textFaint} aria-label="Dismiss">
          <X size={14} />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Confirmation dialog — one reusable modal gating every important HR action
// (status change, resolve, share/unshare, delete attachment, send follow-up,
// etc.). `useConfirm()` gives callers an `ask(config)` promise so any
// handler can simply `await ask({...})` before doing the real work.
// ---------------------------------------------------------------------------

export function ConfirmDialog({ config, onCancel, onConfirm, busy }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  if (!config) return null;
  const danger = config.tone === "danger";
  return (
    <div
      className={"fixed inset-0 z-[80] flex items-center justify-center p-4 backdrop-blur-sm " + t.overlay}
      onClick={onCancel}
    >
      <div
        className={"w-full max-w-sm rounded-3xl border p-5 " + t.card + " " + t.cardShadow}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2.5">
          <span
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
            style={{
              backgroundColor: `${danger ? BRAND.red : BRAND.purple}1f`,
              color: danger ? BRAND.red : BRAND.purple,
            }}
          >
            <AlertCircle size={18} strokeWidth={2.5} />
          </span>
          <h3 className={"text-base font-bold " + t.textPrimary}>{config.title || "Confirm Action"}</h3>
        </div>
        <p className={"mt-3 text-sm leading-relaxed " + t.textMuted}>
          {config.message || "Are you sure you want to perform this action?"}
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={busy}
            className={"rounded-full border px-4 py-2 text-xs font-semibold transition-colors disabled:opacity-50 " + t.pillBorder + " " + t.surfaceSoft + " " + t.surfaceHover + " " + t.textMuted}
          >
            {config.cancelLabel || "Cancel"}
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="rounded-full px-4 py-2 text-xs font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
            style={{ background: danger ? BRAND.red : BRAND.gradient }}
          >
            {busy ? "Working…" : config.confirmLabel || "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function useConfirm() {
  const [config, setConfig] = useState(null);
  const [busy, setBusy] = useState(false);
  const resolverRef = useRef(null);

  const ask = useCallback((cfg) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setConfig(cfg);
    });
  }, []);

  const cancel = useCallback(() => {
    if (busy) return;
    resolverRef.current?.(false);
    setConfig(null);
  }, [busy]);

  const confirm = useCallback(async () => {
    if (!config?.onConfirm) {
      resolverRef.current?.(true);
      setConfig(null);
      return;
    }
    setBusy(true);
    try {
      await config.onConfirm();
      resolverRef.current?.(true);
      setConfig(null);
    } catch {
      // Leave the dialog open — the action's own catch block already
      // surfaced a toast; the person can retry or cancel.
    } finally {
      setBusy(false);
    }
  }, [config]);

  const dialog = <ConfirmDialog config={config} onCancel={cancel} onConfirm={confirm} busy={busy} />;
  return { ask, dialog };
}

// ---------------------------------------------------------------------------
// Attachment row — used by timeline entries and the attachments panel.
// Preview for images, download link for everything else.
// ---------------------------------------------------------------------------

export function AttachmentRow({ attachment, onDelete, t }) {
  const isImg = isImageMime(attachment.mimetype);
  const Icon = isImg ? ImageIcon : FileText;
  return (
    <div className={"flex items-center gap-2 rounded-xl border px-2.5 py-2 " + t.pillBorder + " " + t.surface}>
      {isImg ? (
        <img src={attachment.url} alt={attachment.originalName} className="h-8 w-8 shrink-0 rounded-md object-cover" />
      ) : (
        <span className={"flex h-8 w-8 shrink-0 items-center justify-center rounded-md " + t.surfaceSoft + " " + t.textMuted}>
          <Icon size={14} />
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div className={"truncate text-xs font-semibold " + t.textPrimary}>{attachment.originalName}</div>
        <div className={"text-[10px] " + t.textFaint}>{fmtBytes(attachment.size)}</div>
      </div>
      <a
        href={attachment.url}
        download={attachment.originalName}
        target="_blank"
        rel="noreferrer"
        className={"flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors " + t.surfaceSoft + " " + t.surfaceHover + " " + t.textMuted}
        aria-label="Download"
      >
        <Download size={12} strokeWidth={2.5} />
      </a>
      {onDelete && (
        <button
          onClick={onDelete}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-red-500/10"
          style={{ color: BRAND.red }}
          aria-label="Delete attachment"
        >
          <Trash2 size={12} strokeWidth={2.5} />
        </button>
      )}
    </div>
  );
}